// Footer.js
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Logo from '../../components/Logo';

const whyLinks = [
  { to: '/about', title: 'For families & residents', text: 'Resources and guidance to navigate care with confidence.' },
  // Careers temporarily hidden: { to: '/careers', title: 'For carers', text: 'Training and tools for compassionate, high-quality care.' },
  { to: '/contact', title: 'For providers', text: 'Support with compliance, staffing, and daily operations.' },
];

const serviceLinks = [
  { to: '/services/personal-care', label: 'Daily Living Support' },
  { to: '/services/post-operative-care', label: 'Medical Assistance' },
  { to: '/services/alzheimers-dementia-care', label: 'Memory Care' },
];

const resourceLinks = [
  { to: '/about/faq', label: 'FAQs' },
  { to: '/contact', label: 'Help Centre' },
];

const Footer = () => {
  return (
    <footer className="bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Brand block */}
          <div className="lg:col-span-5 bg-cream rounded-2xl p-8 md:p-10 flex flex-col">
            <div className="mb-6">
              <Logo className="h-10" />
            </div>
            <h3 className="font-display font-light text-ink text-2xl md:text-3xl leading-snug mb-4">
              A Care Philosophy Built on Respect, Connection, and Purpose
            </h3>
            <p className="text-muted text-sm leading-relaxed mb-8 max-w-sm">
              We are committed to providing respectful, safe, and personalised care for every person we
              support, standing with families and care teams every step of the way.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-primary hover:text-primary-dark font-medium text-sm px-6 py-3.5 rounded-full shadow-sm transition-colors mt-auto w-full"
            >
              Get in Touch
              <ArrowRight size={16} />
            </Link>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-10">
            {/* Why Wei Care */}
            <div>
              <h4 className="text-ink font-medium mb-5">Why Wei Care</h4>
              <ul className="space-y-5">
                {whyLinks.map((l) => (
                  <li key={l.title}>
                    <Link to={l.to} className="group flex items-start gap-2">
                      <ArrowRight size={15} className="mt-1 text-primary shrink-0" />
                      <span>
                        <span className="block text-ink text-sm group-hover:text-primary transition-colors">{l.title}</span>
                        <span className="block text-muted text-xs mt-1 leading-relaxed">{l.text}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-ink font-medium mb-5">Services</h4>
              <ul className="space-y-4">
                {serviceLinks.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="flex items-center gap-2 text-muted text-sm hover:text-primary transition-colors">
                      <ArrowRight size={14} className="text-primary" />
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h4 className="text-ink font-medium mb-5">Resources</h4>
              <ul className="space-y-4">
                {resourceLinks.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="flex items-center gap-2 text-muted text-sm hover:text-primary transition-colors">
                      <ArrowRight size={14} className="text-primary" />
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-line mt-14 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-muted text-sm">© 2026 Weicare Pty Ltd (Wei Care) · ABN 40 643 964 121 · 15 Monash Street, Clermont QLD 4721</p>
          <div className="flex items-center gap-6 text-sm text-muted">
            <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy</Link>
            <Link to="/terms-of-service" className="hover:text-primary transition-colors">Terms</Link>
            <Link to="/cookie-policy" className="hover:text-primary transition-colors">Cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
